import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiError } from "../../lib/api";
import { PageHeader, Card, Select, Empty, Badge, Button } from "../../components/admin/UI";
import { downloadXlsx } from "../../lib/download";
import { toast } from "sonner";

const SORTS = [
    { k: "points", l: "Points" },
    { k: "rebounds", l: "Rebonds" },
    { k: "assists", l: "Passes" },
    { k: "steals", l: "Interceptions" },
    { k: "blocks", l: "Contres" },
    { k: "ppg", l: "Pts / match" },
];

export default function StatsLeaderboard() {
    const { activeClubId, activeSeasonId, canManageSports } = useAuth();
    const [teams, setTeams] = useState([]);
    const [players, setPlayers] = useState([]);
    const [filterTeam, setFilterTeam] = useState("");
    const [sortBy, setSortBy] = useState("points");
    const [rows, setRows] = useState([]);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        if (!activeClubId) return;
        const params = { club_id: activeClubId };
        if (activeSeasonId) params.season_id = activeSeasonId;
        api.get("/teams", { params }).then((r) => setTeams(r.data));
        api.get("/players", { params }).then((r) => setPlayers(r.data));
    }, [activeClubId, activeSeasonId]);

    useEffect(() => {
        if (!activeClubId || !activeSeasonId) return;
        const params = { club_id: activeClubId, season_id: activeSeasonId, sort_by: sortBy };
        if (filterTeam) params.team_id = filterTeam;
        api.get("/stats/season/leaderboard", { params }).then((r) => setRows(r.data));
    }, [activeClubId, activeSeasonId, filterTeam, sortBy]);

    const playerName = (id) => {
        const p = players.find((x) => x.id === id);
        return p ? `${p.last_name?.toUpperCase()} ${p.first_name}` : id;
    };
    const teamName = (id) => teams.find((t) => t.id === id)?.name || "—";
    const exportStats = async () => {
        setExporting(true);
        try {
            const params = { club_id: activeClubId, season_id: activeSeasonId };
            if (filterTeam) params.team_id = filterTeam;
            await downloadXlsx("/exports/stats", params, "statistiques.xlsx");
            toast.success("Export Excel téléchargé");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setExporting(false);
        }
    };

    return (
        <div data-testid="stats-page">
            <PageHeader title="Classement statistiques" subtitle="Statistiques cumulées par joueur sur la saison"
                action={canManageSports && <Button variant="secondary" onClick={exportStats} disabled={exporting || !activeSeasonId}>
                    {exporting ? "Export…" : "Exporter Excel"}
                </Button>} />

            <div className="flex flex-wrap gap-3 mb-4">
                <Select label="Équipe" value={filterTeam} onChange={(e) => setFilterTeam(e.target.value)} className="w-56">
                    <option value="">Toutes</option>
                    {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </Select>
                <Select label="Trier par" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="w-56">
                    {SORTS.map((s) => <option key={s.k} value={s.k}>{s.l}</option>)}
                </Select>
            </div>

            {rows.length === 0 ? (
                <Empty title="Aucune statistique" hint="Saisissez des stats sur les matchs joués pour alimenter le classement." />
            ) : (
                <Card>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50">
                                <tr className="text-left">
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">#</th>
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">Joueur</th>
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">Équipe</th>
                                    <th className="px-4 py-3 text-center text-xs uppercase tracking-wider text-slate-500">M.</th>
                                    <th className="px-4 py-3 text-center text-xs uppercase tracking-wider text-slate-500">PTS</th>
                                    <th className="px-4 py-3 text-center text-xs uppercase tracking-wider text-slate-500">MIN</th>
                                    <th className="px-4 py-3 text-center text-xs uppercase tracking-wider text-slate-500">REB</th>
                                    <th className="px-4 py-3 text-center text-xs uppercase tracking-wider text-slate-500">PD</th>
                                    <th className="px-4 py-3 text-center text-xs uppercase tracking-wider text-slate-500">INT</th>
                                    <th className="px-4 py-3 text-center text-xs uppercase tracking-wider text-slate-500">CT</th>
                                    <th className="px-4 py-3 text-center text-xs uppercase tracking-wider text-slate-500">PTS/M</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, idx) => (
                                    <tr key={r.player_id} className="border-t border-slate-100 hover:bg-slate-50">
                                        <td className="px-4 py-3 font-bold">
                                            {idx === 0 ? <Badge color="orange">🥇</Badge> : idx === 1 ? <Badge color="slate">🥈</Badge> : idx === 2 ? <Badge color="amber">🥉</Badge> : idx + 1}
                                        </td>
                                        <td className="px-4 py-3 font-semibold text-slate-900">{playerName(r.player_id)}</td>
                                        <td className="px-4 py-3"><Badge color="slate">{teamName(r.team_id)}</Badge></td>
                                        <td className="px-4 py-3 text-center">{r.games}</td>
                                        <td className="px-4 py-3 text-center font-bold text-orange-600">{r.points}</td>
                                        <td className="px-4 py-3 text-center">{r.minutes || 0}</td>
                                        <td className="px-4 py-3 text-center">{r.rebounds}</td>
                                        <td className="px-4 py-3 text-center">{r.assists}</td>
                                        <td className="px-4 py-3 text-center">{r.steals}</td>
                                        <td className="px-4 py-3 text-center">{r.blocks}</td>
                                        <td className="px-4 py-3 text-center font-semibold">{r.ppg}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}
        </div>
    );
}
